import { Plus, Trash } from "lucide-react";
import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import { useFormik, FormikProvider, FieldArray } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import {
  getNominationDetails,
  updateNominationDetails,
  getFamilyMembersList,
} from "../../../ApiHelper/url_Helpers.js";
import Loader from "../../../Components/Loader";
import { RiseLoader } from "react-spinners";

const validationSchema = Yup.object().shape({
  epf: Yup.array()
    .of(
      Yup.object().shape({
        nominee: Yup.string().required("Required"),
        percentage: Yup.number()
          .typeError("Must be a number")
          .required("Required")
          .min(1)
          .max(100),
      }),
    )
    .test(
      "total-100",
      "Total EPF percentage must be 100%",
      (val) =>
        val?.reduce((sum, item) => sum + (Number(item.percentage) || 0), 0) ===
        100,
    ),
  eps: Yup.object().shape({ nominee: Yup.string().required("Required") }),
  esi: Yup.object().shape({ nominee: Yup.string().required("Required") }),
  gratuity: Yup.array()
    .of(
      Yup.object().shape({
        nominee: Yup.string().required("Required"),
        percentage: Yup.number()
          .typeError("Must be a number")
          .required("Required")
          .min(1)
          .max(100),
      }),
    )
    .test(
      "total-100",
      "Total Gratuity percentage must be 100%",
      (val) =>
        val?.reduce((sum, item) => sum + (Number(item.percentage) || 0), 0) ===
        100,
    ),
});

const NominationDetails = forwardRef(({ onEditStatusChange, onDirtyChange }, ref)  => {
  const [familyMembers, setFamilyMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitError, setSubmitError] = useState(null);
  const [isEditable, setIsEditable] = useState(false); // Lock state
  
  const [initialFormValues, setInitialFormValues] = useState({
    epf: [{ nominee: "", percentage: "", nominee_name: "" }],
    eps: { nominee: "", nominee_name: "" },
    esi: { nominee: "", nominee_name: "" },
    gratuity: [{ nominee: "", percentage: "", nominee_name: "" }],
  });

  const familyOptions = useMemo(
    () =>
      familyMembers.map((m) => ({
        value: String(m.id || m.name),
        label: m.member_name || m.name || "Unknown Member",
      })),
    [familyMembers],
  );

  const customSelectStyles = (isError, readOnly) => ({
    control: (base, state) => ({
      ...base,
      minHeight: "38px",
      borderRadius: "0.5rem",
      fontSize: "14px",
      backgroundColor: readOnly ? "#F3F4F6" : "#FAFBFD",
      borderColor: isError
        ? "#ef4444"
        : state.isFocused
          ? "#00B8D9"
          : "#d1d5db",
      boxShadow: state.isFocused ? "0 0 0 1px #00B8D9" : "none",
      cursor: readOnly ? "not-allowed" : "default",
      "&:hover": { borderColor: state.isFocused ? "#00B8D9" : "#d1d5db" },
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  });

  const getSelectedOption = (nomineeId, nomineeName, debugLabel) => {
    const selectedOption = familyOptions.find(
      (opt) => String(opt.value) === String(nomineeId),
    ) || (nomineeId ? {
      value: String(nomineeId),
      label: nomineeName || "Unknown",
    } : null);
    return selectedOption;
  };

  const formik = useFormik({
    initialValues: initialFormValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: () => {},
  });


  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(formik.dirty);
    }
  }, [formik.dirty, onDirtyChange])

  const fetchData = async () => {
    try {
      setLoading(true);
      const [nomRes, familyRes] = await Promise.all([
        getNominationDetails(),
        getFamilyMembersList(),
      ]);

      // Set editable status based on API response
      const canEdit = nomRes?.nominations?.action?.isEdit ?? false;
      setIsEditable(canEdit);
      onEditStatusChange(canEdit)
      const familyData =
        familyRes?.family_members?.data || familyRes?.data || [];
      setFamilyMembers(familyData);

      const rawNomData = nomRes?.nominations?.data || [];

      if (Array.isArray(rawNomData) && rawNomData.length > 0) {
        const mapped = {
          epf: rawNomData
            .filter((n) => n.nomination_type === "epf")
            .map((n) => ({
              nominee: n.nominee ? String(n.nominee) : (n.nominee_id ? String(n.nominee_id) : ""),
              nominee_name: n.nominee_name || "",
              percentage: n.percentage,
            })),
          gratuity: rawNomData
            .filter((n) => n.nomination_type === "gratuity")
            .map((n) => ({
              nominee: n.nominee ? String(n.nominee) : (n.nominee_id ? String(n.nominee_id) : ""),
              nominee_name: n.nominee_name || "",
              percentage: n.percentage,
            })),
          eps: (() => {
            const f = rawNomData.find((n) => n.nomination_type === "eps");
            return {
              nominee: f?.nominee ? String(f.nominee) : (f?.nominee_id ? String(f.nominee_id) : ""),
              nominee_name: f?.nominee_name || "",
            };
          })(),
          esi: (() => {
            const f = rawNomData.find((n) => n.nomination_type === "esi");
            return {
              nominee: f?.nominee ? String(f.nominee) : (f?.nominee_id ? String(f.nominee_id) : ""),
              nominee_name: f?.nominee_name || "",
            };
          })(),
        };

        // Fallback agar API se data empty aaye toh default row dikhane ke liye
        if (mapped.epf.length === 0)
          mapped.epf = [{ nominee: "", percentage: "", nominee_name: "" }];
        if (mapped.gratuity.length === 0)
          mapped.gratuity = [{ nominee: "", percentage: "", nominee_name: "" }];

        setInitialFormValues(mapped);
      }
    } catch (err) {
      console.error("Fetch Error:", err);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // --- Expose state and save function to Parent ---
  useImperativeHandle(ref, () => ({
    isEditable: isEditable, // Parent reads this to show/hide Save buttons
    handleChildSave: async () => {
      if (!isEditable)
        return { success: false, error: "Editing is not allowed" };

      const errors = await formik.validateForm();
      formik.setTouched({
        epf: formik.values.epf.map(() => ({ nominee: true, percentage: true })),
        eps: { nominee: true },
        esi: { nominee: true },
        gratuity: formik.values.gratuity.map(() => ({
          nominee: true,
          percentage: true,
        })),
      });

        if (Object.keys(errors).length > 0) {
          setSubmitError("Please fill all required fields correctly.");
          return { success: false, error: "Validation failed" };
        }

      try {
        setSubmitError(null);
        const payload = [
          ...formik.values.epf
            .filter((n) => n.nominee)
            .map((n) => ({
              nominee: n.nominee,
              percentage: Number(n.percentage),
              nomination_type: "epf",
            })),
          ...formik.values.gratuity
            .filter((n) => n.nominee)
            .map((n) => ({
              nominee: n.nominee,
              percentage: Number(n.percentage),
              nomination_type: "gratuity",
            })),
          ...(formik.values.eps.nominee
            ? [
                {
                  nominee: formik.values.eps.nominee,
                  // percentage: 100,
                  nomination_type: "eps",
                },
              ]
            : []),
          ...(formik.values.esi.nominee
            ? [
                {
                  nominee: formik.values.esi.nominee,
                  // percentage: 100,
                  nomination_type: "esi",
                },
              ]
            : []),
        ];

        await updateNominationDetails({ nominations: payload });
        await fetchData(); // Refresh data to lock UI
        return { success: true };
      } catch (err) {
        const apiMsg = err.response?.data?.message || "Failed to save details.";
        setSubmitError(apiMsg);
        return { success: false, error: apiMsg };
      }
    },
  }));

  const ErrorMsg = ({ touched, error }) =>
    touched && error ? (
      <span className="text-[10px] md:text-xs text-red-500 mt-1 px-1 font-medium">
        {Array.isArray(error) ? error[0] : error}
      </span>
    ) : null;

  return (
    <FormikProvider value={formik}>
      <div className="flex flex-col gap-3  w-full max-w-full overflow-x-hidden min-w-0 pb-5">
        {loading ? (
          <div
            className={`flex items-center justify-center w-full h-full min-h-[350px]`}
          >
            <RiseLoader color="#35e6e9" />
          </div>
        ) : (
          <div>
            {submitError && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-2 rounded-lg text-sm font-semibold mx-1">
                {submitError}
              </div>
            )}

            {/* EPF Nominations */}
            <div className="border border-black/6 border-2 bg-white rounded-lg pb-2 overflow-hidden  mb-5 w-full min-w-0 shadow-sm">
              <div className="flex justify-between items-center px-2 md:px-5 border-b py-2 md:py-3 bg-gray-50/30">
                <h3 className="font-semibold text-xs md:text-[16px]">
                  EPF Nominee Details
                </h3>
                {typeof formik.errors.epf === "string" && (
                  <span className="text-[10px] text-red-500 font-bold uppercase">
                    {formik.errors.epf}
                  </span>
                )}
              </div>
              <FieldArray name="epf">
                {({ push, remove }) => (
                  <>
                    {formik.values.epf.map((item, index) => (
                      <div key={index}>
                        <div className="flex justify-between items-center px-2 md:px-5 mt-2 md:mt-4">
                          <h4 className="font-semibold text-xs md:text-[14px] text-gray-400 uppercase tracking-tight">
                            Nominee {index + 1}
                          </h4>
                          {isEditable && formik.values.epf.length > 1 && (
                            <button
                              type="button"
                              onClick={() => remove(index)}
                              className="text-red-500 hover:text-red-600 text-[11px] md:text-xs font-semibold uppercase tracking-wider transition-colors"
                            >
                              <Trash size={18}/> 
                            </button>
                          )}
                        </div>
                        <div className="border-t border-dashed border-gray-300 mx-2 md:mx-5 mt-2"></div>
                        <div className="flex flex-col md:flex-row gap-3 md:gap-4 py-2 md:py-4 px-2 md:px-6 w-full">
                          <div className="flex flex-col w-full md:w-2/3">
                            <label className="font-semibold text-gray-600 px-1 text-[13px] md:text-sm mb-1">
                              Name{" "}
                              {isEditable && (
                                <span className="text-red-500">*</span>
                              )}
                            </label>
                            <Select
                              isDisabled={!isEditable}
                              instanceId={`epf-select-${index}`}
                              options={familyOptions}
                              value={getSelectedOption(item.nominee, item.nominee_name, `EPF-${index}`)}
                              onChange={(opt) => {
                                formik.setFieldValue(
                                  `epf.${index}.nominee`,
                                  opt ? opt.value : "",
                                );
                                formik.setFieldValue(
                                  `epf.${index}.nominee_name`,
                                  opt ? opt.label : "",
                                );
                              }}
                              styles={customSelectStyles(
                                formik.touched.epf?.[index]?.nominee &&
                                  formik.errors.epf?.[index]?.nominee,
                                !isEditable,
                              )}
                              placeholder="Select Member"
                              menuPortalTarget={document.body}
                            />
                            <ErrorMsg
                              touched={formik.touched.epf?.[index]?.nominee}
                              error={formik.errors.epf?.[index]?.nominee}
                            />
                          </div>
                          <div className="flex flex-col w-full md:w-1/3">
                            <label className="font-semibold text-gray-600 px-1 text-[13px] md:text-sm mb-1">
                              Nomination %{" "}
                              {isEditable && (
                                <span className="text-red-500">*</span>
                              )}
                            </label>
                            <input
                              readOnly={!isEditable}
                              type="number"
                              name={`epf.${index}.percentage`}
                              value={item.percentage ?? ""}
                              onChange={formik.handleChange}
                              placeholder="e.g. 50"
                              className={`border rounded-lg px-2 md:px-4 py-1.5 md:py-[7px] text-xs md:text-sm w-full outline-none transition-all ${!isEditable ? "bg-gray-100 cursor-not-allowed text-gray-500" : "bg-[#FAFBFD] focus:ring-1 focus:ring-[#00B8D9]"} ${formik.touched.epf?.[index]?.percentage && formik.errors.epf?.[index]?.percentage ? "border-red-500 bg-red-50" : "border-gray-300"}`}
                            />
                            <ErrorMsg
                              touched={formik.touched.epf?.[index]?.percentage}
                              error={formik.errors.epf?.[index]?.percentage}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                    {isEditable && (
                      <p
                        onClick={() => push({ nominee: "", percentage: "", nominee_name: "" })}
                        className="flex justify-center items-center gap-2 font-bold text-[#00B8D9] text-xs md:text-[14px] py-2 md:py-4 cursor-pointer hover:underline"
                      >
                        <Plus size={18} /> Add Another Member
                      </p>
                    )}
                  </>
                )}
              </FieldArray>
            </div>

            {/* EPS & ESI Nomination Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
              <div className="border border-black/6 border-2 bg-white rounded-lg pb-2 overflow-hidden shadow-sm">
                <h3 className="font-semibold px-2 md:px-5 border-b py-2 md:py-3 text-xs md:text-[16px] bg-gray-50/30">
                  EPS Nominee
                </h3>
                <div className="flex flex-col py-4 px-2 md:px-6">
                  <label className="font-semibold text-gray-600 px-1 text-[13px] md:text-sm mb-1">
                    Name {isEditable && <span className="text-red-500">*</span>}
                  </label>
                  <Select
                    isDisabled={!isEditable}
                    instanceId="eps-select"
                    options={familyOptions}
                    value={getSelectedOption(formik.values.eps.nominee, formik.values.eps.nominee_name, "EPS")}
                    onChange={(opt) => {
                      formik.setFieldValue("eps.nominee", opt ? opt.value : "");
                      formik.setFieldValue("eps.nominee_name", opt ? opt.label : "");
                    }}
                    styles={customSelectStyles(
                      formik.touched.eps?.nominee && formik.errors.eps?.nominee,
                      !isEditable,
                    )}
                    placeholder="Select Member"
                    menuPortalTarget={document.body}
                  />
                  <ErrorMsg
                    touched={formik.touched.eps?.nominee}
                    error={formik.errors.eps?.nominee}
                  />
                </div>
              </div>

              <div className="border border-black/6 border-2 bg-white rounded-lg pb-2 overflow-hidden shadow-sm">
                <h3 className="font-semibold px-2 md:px-5 border-b py-2 md:py-3 text-xs md:text-[16px] bg-gray-50/30">
                  ESI Nominee
                </h3>
                <div className="flex flex-col py-4 px-2 md:px-6">
                  <label className="font-semibold text-gray-600 px-1 text-[13px] md:text-sm mb-1">
                    Name {isEditable && <span className="text-red-500">*</span>}
                  </label>
                  <Select
                    isDisabled={!isEditable}
                    instanceId="esi-select"
                    options={familyOptions}
                    value={getSelectedOption(formik.values.esi.nominee, formik.values.esi.nominee_name, "ESI")}
                    onChange={(opt) => {
                      formik.setFieldValue("esi.nominee", opt ? opt.value : "");
                      formik.setFieldValue("esi.nominee_name", opt ? opt.label : "");
                    }}
                    styles={customSelectStyles(
                      formik.touched.esi?.nominee && formik.errors.esi?.nominee,
                      !isEditable,
                    )}
                    placeholder="Select Member"
                    menuPortalTarget={document.body}
                  />
                  <ErrorMsg
                    touched={formik.touched.esi?.nominee}
                    error={formik.errors.esi?.nominee}
                  />
                </div>
              </div>
            </div>

            {/* Gratuity Nomination */}
            <div className="border border-black/6 border-2 bg-white rounded-lg pb-2 overflow-hidden w-full mt-8  min-w-0 shadow-sm">
              <div className="flex justify-between items-center px-2 md:px-5 border-b py-2 md:py-3 bg-gray-50/30">
                <h3 className="font-bold text-xs md:text-[16px]">
                  Gratuity Nominee
                </h3>
                {typeof formik.errors.gratuity === "string" && (
                  <span className="text-[10px] text-red-500 font-bold uppercase">
                    {formik.errors.gratuity}
                  </span>
                )}
              </div>
              <FieldArray name="gratuity">
                {({ push, remove }) => (
                  <>
                    {formik.values.gratuity.map((item, index) => (
                      <div key={index}>
                        <div className="flex justify-between items-center px-2 md:px-5 mt-2 md:mt-4">
                          <h4 className="font-semibold text-xs md:text-[14px] text-gray-400 uppercase tracking-tight">
                            Nominee {index + 1}
                          </h4>
                          {isEditable && formik.values.gratuity.length > 1 && (
                            <button
                              type="button"
                              onClick={() => remove(index)}
                              className="text-red-500 hover:text-red-600 text-[11px] md:text-xs font-semibold uppercase tracking-wider transition-colors"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        <div className="border-t border-dashed border-gray-300 mx-2 md:mx-5 mt-2"></div>
                        <div className="flex flex-col md:flex-row gap-3 md:gap-4 py-2 md:py-4 px-2 md:px-6 w-full">
                          <div className="flex flex-col w-full md:w-2/3">
                            <label className="font-semibold text-gray-600 px-1 text-[13px] md:text-sm mb-1">
                              Name{" "}
                              {isEditable && (
                                <span className="text-red-500">*</span>
                              )}
                            </label>
                            <Select
                              isDisabled={!isEditable}
                              instanceId={`gratuity-select-${index}`}
                              options={familyOptions}
                              value={getSelectedOption(item.nominee, item.nominee_name, `Gratuity-${index}`)}
                              onChange={(opt) => {
                                formik.setFieldValue(
                                  `gratuity.${index}.nominee`,
                                  opt ? opt.value : "",
                                );
                                formik.setFieldValue(
                                  `gratuity.${index}.nominee_name`,
                                  opt ? opt.label : "",
                                );
                              }}
                              styles={customSelectStyles(
                                formik.touched.gratuity?.[index]?.nominee &&
                                  formik.errors.gratuity?.[index]?.nominee,
                                !isEditable,
                              )}
                              placeholder="Select Member"
                              menuPortalTarget={document.body}
                            />
                            <ErrorMsg
                              touched={
                                formik.touched.gratuity?.[index]?.nominee
                              }
                              error={formik.errors.gratuity?.[index]?.nominee}
                            />
                          </div>
                          <div className="flex flex-col w-full md:w-1/3">
                            <label className="font-semibold text-gray-600 px-1 text-[13px] md:text-sm mb-1">
                              Nomination %{" "}
                              {isEditable && (
                                <span className="text-red-500">*</span>
                              )}
                            </label>
                            <input
                              readOnly={!isEditable}
                              type="number"
                              name={`gratuity.${index}.percentage`}
                              value={item.percentage ?? ""}
                              onChange={formik.handleChange}
                              placeholder="e.g. 100"
                              className={`border rounded-lg px-2 md:px-4 py-1.5 md:py-[7px] text-xs md:text-sm w-full outline-none transition-all ${!isEditable ? "bg-gray-100 cursor-not-allowed text-gray-500" : "bg-[#FAFBFD] focus:ring-1 focus:ring-[#00B8D9]"} ${formik.touched.gratuity?.[index]?.percentage && formik.errors.gratuity?.[index]?.percentage ? "border-red-500 bg-red-50" : "border-gray-300"}`}
                            />
                            <ErrorMsg
                              touched={
                                formik.touched.gratuity?.[index]?.percentage
                              }
                              error={
                                formik.errors.gratuity?.[index]?.percentage
                              }
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                    {isEditable && (
                      <p
                        onClick={() => push({ nominee: "", percentage: "", nominee_name: "" })}
                        className="flex justify-center items-center gap-2 font-bold text-[#00B8D9] text-xs md:text-[14px] py-2 md:py-4 cursor-pointer hover:underline"
                      >
                        <Plus size={18} /> Add Another Member
                      </p>
                    )}
                  </>
                )}
              </FieldArray>
            </div>
          </div>
        )}
      </div>
    </FormikProvider>
  );
});

export default NominationDetails;
